import React, { useState, useEffect } from 'react';
import { MapPin, Clock, Heart, Check, X, Star, Navigation } from 'lucide-react';
import { Location, UserLocation } from '../types';
import { getImageUrl } from '../utils/images';
import { getCachedRoute } from '../utils/routes';
import { getVisitStats } from '../utils/supabase';
import { getWasVerb, getLikedVerb, getSatisfiedWord } from '../utils/grammar';

interface TravelLocationCardProps {
  location: Location;
  userLocation?: UserLocation | null;
  onSelect: (location: Location) => void;
  onTagClick: (tag: string) => void;
}

export const TravelLocationCard: React.FC<TravelLocationCardProps> = ({
  location,
  userLocation,
  onSelect,
  onTagClick,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [distance, setDistance] = useState<number | null>(null);
  const [driveTime, setDriveTime] = useState<number | null>(null);
  const [stats, setStats] = useState<{ total: number; likes: number; dislikes: number } | null>(null);

  useEffect(() => {
    if (location.id) {
      getVisitStats(location.id).then(setStats).catch(console.error);
    }
  }, [location.id]);

  useEffect(() => {
    if (userLocation && location.coordinates) {
      getCachedRoute(userLocation, location.coordinates)
        .then(({ distance, duration }) => {
          setDistance(distance);
          setDriveTime(duration);
        })
        .catch(console.error);
    }
  }, [userLocation, location.coordinates]);

  useEffect(() => {
    if (isHovered && location.images.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) =>
          prev === location.images.length - 1 ? 0 : prev + 1
        );
      }, 2500);
      return () => clearInterval(interval);
    }
    return () => setCurrentImageIndex(0);
  }, [isHovered, location.images.length]);

  const formatDuration = (minutes: number) => {
    if (minutes > 60) {
      const hours = minutes / 60;
      const roundedHours = Math.round(hours * 10) / 10;
      return `${Number.isInteger(roundedHours) ? Math.floor(roundedHours) : roundedHours} г.`;
    }
    return `${minutes} хв`;
  };

  const reviews = stats
    ? {
        positive: stats.total,
        likes: stats.likes,
        dislikes: stats.dislikes,
        rating: stats.total ? Math.round((stats.likes / stats.total) * 100) : 0,
      }
    : undefined;

  const hasReviews =
    reviews && (reviews.likes > 0 || reviews.dislikes > 0 || reviews.positive > 0);

  return (
    <div
      className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 cursor-pointer overflow-hidden border border-gray-100 hover:border-primary/20 transform hover:-translate-y-1 flex flex-col h-full"
      onClick={() => onSelect(location)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Section */}
      <div className="relative overflow-hidden">
        {location.images && location.images.length > 0 ? (
          <div className="aspect-[4/3] relative">
            <img
              src={getImageUrl(location.images[currentImageIndex])}
              alt={location.name}
              className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
        ) : (
          <div className="aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <span className="text-gray-500 text-sm font-medium">Няма фотаздымкаў</span>
          </div>
        )}
        
        {/* Image Counter */}
        {location.images.length > 1 && (
          <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full font-medium">
            {currentImageIndex + 1}/{location.images.length}
          </div>
        )}

        {/* Distance Badge */}
        {distance !== null && (
          <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-gray-800 text-xs px-2.5 py-1 rounded-full font-semibold shadow-sm">
            {distance} км
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-5 flex flex-col flex-1">
        {/* Title */}
        <div className="mb-3">
          <h3 className="text-lg font-bold text-gray-900 line-clamp-2 leading-tight mb-2 group-hover:text-primary transition-colors duration-300">
            {location.name}
          </h3>
          
          {/* Distance and Time Info */}
          {(distance !== null || driveTime !== null) && (
            <div className="flex items-center gap-4 text-sm text-gray-600">
              {distance !== null && (
                <div className="flex items-center gap-1.5">
                  <Navigation className="w-4 h-4 text-primary" />
                  <span className="font-medium">{distance} км</span>
                </div>
              )}
              {driveTime !== null && (
                <div className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-primary" />
                  <span className="font-medium">{formatDuration(driveTime)}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Description */}
        <p className="text-sm text-gray-700 line-clamp-3 leading-relaxed mb-4 flex-grow">
          {location.shortDescription}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {location.tags.slice(0, 3).map((tag, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                onTagClick(tag);
              }}
              className="bg-primary/10 hover:bg-primary/20 text-primary px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 hover:scale-105 transform border border-primary/20 hover:border-primary/30"
            >
              #{tag}
            </button>
          ))}
          {location.tags.length > 3 && (
            <span className="text-xs text-gray-500 px-2 py-1.5 bg-gray-100 rounded-full">
              +{location.tags.length - 3}
            </span>
          )}
        </div>

        {/* Stats Section - Fixed Height */}
        <div className="pt-3 border-t border-gray-100 h-12 flex items-center">
          {hasReviews ? (
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-4 text-xs">
                {reviews!.positive > 0 && (
                  <div className="flex items-center gap-1.5 text-green-600">
                    <Check className="w-4 h-4" />
                    <span className="font-semibold">{reviews!.positive}</span>
                    <span className="text-gray-500">{getWasVerb(reviews!.positive)} тут</span>
                  </div>
                )}
                {reviews!.likes > 0 && (
                  <div className="flex items-center gap-1.5 text-red-500">
                    <Heart className="w-4 h-4 fill-current" />
                    <span className="font-semibold">{reviews!.likes}</span>
                    <span className="text-gray-500">{getLikedVerb(reviews!.likes)}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 text-primary">
                <Star className="w-4 h-4 fill-current" />
                <span className="font-bold text-sm">{reviews!.rating}%</span>
                <span className="text-xs text-gray-500">{getSatisfiedWord()}</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center w-full">
              <span className="text-xs text-gray-500 italic">Ацэнак пакуль няма</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};